import React, { useState, useEffect, FormEvent } from 'react';
import { m, AnimatePresence } from 'motion/react';
import { CheckCircle2, Star, Send } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';

const feedbackTranslations = {
  EN: {
    title: "Thank you for working with us",
    introGeneric: "We would love to hear from you! Leaving a review helps us improve our service and reach more clients like yourself!",
    introPersonalized: (name: string) => `Hi ${name}, we would love to hear from you! Leaving a review helps us improve our service and reach more clients like yourself!`,
    firstName: "First name",
    lastName: "Last name",
    email: "E-mail",
    satisfaction: "How satisfied are you with our service?",
    lowSatisfaction: "Low satisfaction",
    highSatisfaction: "High satisfaction",
    feedbackShort: "Anything else you want us to know?",
    feedbackShortSub: "(This will not be shared)",
    feedbackShortPlaceholder: "Your Feedback",
    reviewText: "Your review",
    reviewTextPlaceholder: "Share your experience...",
    consent: "I agree that my review may be published (using my full first name and initial of last name)",
    consentSub: "You do not have to agree in order to send us your feedback",
    submit: "Send feedback",
    sending: "Sending...",
    successTitle: "Feedback Sent",
    successMsg: "Thank you for your feedback — we really appreciate it.",
    successBtn: "Send another response",
    footer: "Professional Tax Advisory"
  },
  DE: {
    title: "Vielen Dank für die Zusammenarbeit",
    introGeneric: "Wir freuen uns über dein Feedback! Eine Review zu schreiben hilft uns, unseren Service zu verbessern und weitere Klient:innen wie dich zu finden!",
    introPersonalized: (name: string) => `Hallo ${name}, wir freuen uns über dein Feedback! Eine Review zu schreiben hilft uns, unseren Service zu verbessern und weitere Klient:innen wie dich zu finden!`,
    firstName: "Vorname",
    lastName: "Nachname",
    email: "E-Mail",
    satisfaction: "Wie zufrieden bist du mit unserem Service?",
    lowSatisfaction: "Geringe Zufriedenheit",
    highSatisfaction: "Hohe Zufriedenheit",
    feedbackShort: "Sollen wir noch etwas wissen?",
    feedbackShortSub: "(Das wird nicht veröffentlicht)",
    feedbackShortPlaceholder: "Dein Feedback",
    reviewText: "Deine Review",
    reviewTextPlaceholder: "Teile deine Erfahrungen...",
    consent: "Ich stimme zu, dass meine Review veröffentlicht werden darf (unter meinem Vornamen und ersten Buchstaben des Nachnamens)",
    consentSub: "Du musst nicht zustimmen, um uns dein Feedback zu senden",
    submit: "Feedback senden",
    sending: "Wird gesendet...",
    successTitle: "Feedback gesendet",
    successMsg: "Vielen Dank für dein Feedback – wir wissen es sehr zu schätzen.",
    successBtn: "Weitere Antwort senden",
    footer: "Professionelle Steuerberatung"
  }
};

const Feedback = () => {
  // /feedback is a flat, unprefixed route deliberately excluded from the
  // DE/EN URL migration (see App.tsx), so the global language context always
  // resolves to 'DE' here (it's derived from the URL path, and this path
  // never starts with /en). This page needs its own toggleable language —
  // seeded from useLanguage() as a reasonable default, then driven locally so
  // both the ?lang= URL param and the in-page EN/DE toggle actually work.
  // setLanguage() is still called to keep the remembered preference in sync
  // for whichever page the visitor goes to next.
  const { language: contextLanguage, setLanguage } = useLanguage();
  const [language, setPageLanguage] = useState<'EN' | 'DE'>(contextLanguage);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [satisfaction, setSatisfaction] = useState<number | null>(null);
  const [feedbackShort, setFeedbackShort] = useState('');
  const [reviewText, setReviewText] = useState('');
  const [consentPublish, setConsentPublish] = useState(true);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const t = feedbackTranslations[language as keyof typeof feedbackTranslations];

  // Read URL parameters on mount
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const fName = params.get('first_name') || params.get('firstName');
    const lName = params.get('last_name') || params.get('lastName');
    const emailParam = params.get('email');
    const langParam = params.get('lang')?.toUpperCase();
    
    if (fName) setFirstName(fName);
    if (lName) setLastName(lName);
    if (emailParam) setEmail(emailParam);
    if (langParam === 'EN' || langParam === 'DE') {
      setPageLanguage(langParam as 'EN' | 'DE');
      setLanguage(langParam as 'EN' | 'DE');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setLanguage]);

  const LanguageToggle = () => (
    <div className="flex items-center gap-1 bg-pstax-bg/50 p-1 rounded-full border border-pstax-border">
      {(['EN', 'DE'] as const).map((lang) => (
        <button
          key={lang}
          type="button"
          onClick={() => {
            setPageLanguage(lang);
            setLanguage(lang);
          }}
          className={`px-3 py-1 text-[11px] font-bold rounded-full transition-all ${
            language === lang 
              ? 'bg-pstax-blue text-pstax-navy shadow-sm' 
              : 'text-pstax-navy/50 hover:text-pstax-navy'
          }`}
        >
          {lang}
        </button>
      ))}
    </div>
  );

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (satisfaction === null) return;

    setIsSubmitting(true);

    const submissionData = {
      first_name: firstName,
      last_name: lastName,
      email: email,
      satisfaction_score: satisfaction,
      feedback: feedbackShort,
      review: reviewText,
      consent_publish: consentPublish,
      submitted_at: new Date().toISOString(),
      language: language
    };

    try {
      const response = await fetch('/api/feedback', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(submissionData),
      });

      if (!response.ok) {
        throw new Error('Failed to submit feedback');
      }

      setIsSubmitting(false);
      setIsSubmitted(true);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (error) {
      console.error('Error submitting feedback:', error);
      setIsSubmitting(false);
      // Fallback: still show success for now if it's a minor error
      setIsSubmitted(true);
    }
  };

  if (isSubmitted) {
    return (
      <div className="min-h-[80vh] flex items-center justify-center p-6 relative">
        <div className="absolute top-6 right-6">
          <LanguageToggle />
        </div>
        <m.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="max-w-[520px] w-full bg-white rounded-[24px] border border-pstax-border shadow-xl p-10 text-center space-y-6"
        >
          <div className="flex justify-center mb-6">
            <img 
              src="/regenerated_image_1777641628772.webp" 
              alt="PSTax Logo" 
              className="h-12 w-auto"
              decoding="async"
            />
          </div>
          <div className="flex justify-center">
            <div className="w-20 h-20 bg-emerald-50 rounded-full flex items-center justify-center">
              <CheckCircle2 className="w-12 h-12 text-emerald-500" />
            </div>
          </div>
          <h2 className="text-3xl font-display font-bold text-pstax-navy tracking-tight">
            {t.successTitle}
          </h2>
          <p className="text-pstax-gray leading-relaxed text-lg">
            {t.successMsg}
          </p>
          <button 
            type="button"
            onClick={() => setIsSubmitted(false)}
            className="text-pstax-blue font-semibold hover:text-pstax-navy transition-colors"
          >
            {t.successBtn}
          </button>
        </m.div>
      </div>
    );
  }

  return (
    <div className="min-h-[90vh] py-12 px-6 flex items-center justify-center relative">
      <div className="absolute top-6 right-6 z-10">
        <LanguageToggle />
      </div>
      <div className="max-w-[560px] w-full bg-white rounded-[32px] border border-pstax-border shadow-2xl p-8 md:p-12 relative overflow-hidden">
        {/* Background Accent */}
        <div className="absolute top-0 left-0 right-0 h-2 bg-pstax-blue" />
        
        {/* Header */}
        <header className="mb-10 text-center">
          <div className="flex justify-center mb-8">
            <img 
              src="/regenerated_image_1777641628772.webp" 
              alt="PSTax Logo" 
              className="h-16 w-auto"
              decoding="async"
            />
          </div>
          
          <h1 className="text-3xl font-display font-bold text-pstax-navy tracking-tight mb-4">
            {t.title}
          </h1>
          <p className="text-pstax-gray leading-relaxed">
            {firstName ? t.introPersonalized(firstName) : t.introGeneric}
          </p>
        </header>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <label htmlFor="first_name" className="text-xs font-bold text-pstax-gray uppercase tracking-widest px-1">
                {t.firstName}
              </label>
              <input
                type="text"
                id="first_name"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                required
                className="w-full px-4 py-3 bg-pstax-bg/50 border border-pstax-border rounded-xl focus:outline-none focus:border-pstax-blue focus:ring-4 focus:ring-pstax-blue/10 transition-all font-medium"
                placeholder={t.firstName}
              />
            </div>
            <div className="space-y-2">
              <label htmlFor="last_name" className="text-xs font-bold text-pstax-gray uppercase tracking-widest px-1">
                {t.lastName}
              </label>
              <input
                type="text"
                id="last_name"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                required
                className="w-full px-4 py-3 bg-pstax-bg/50 border border-pstax-border rounded-xl focus:outline-none focus:border-pstax-blue focus:ring-4 focus:ring-pstax-blue/10 transition-all font-medium"
                placeholder={t.lastName}
              />
            </div>
          </div>

          <input type="hidden" id="email" value={email} />

          <div className="space-y-4">
            <label className="text-xs font-bold text-pstax-gray uppercase tracking-widest px-1 block">
              {t.satisfaction}
            </label>
            <div className="flex justify-between gap-2">
              {[1, 2, 3, 4, 5].map((num) => (
                <button
                  key={num}
                  type="button"
                  onClick={() => setSatisfaction(num)}
                  className={`flex-1 h-14 flex items-center justify-center rounded-xl text-xl font-bold transition-all border ${
                    satisfaction === num
                      ? 'bg-pstax-navy text-white border-pstax-navy shadow-lg scale-105'
                      : 'bg-pstax-bg/50 text-pstax-navy border-pstax-border hover:border-pstax-blue hover:bg-white'
                  }`}
                >
                  {num}
                </button>
              ))}
            </div>
            <div className="flex justify-between text-[10px] font-bold text-pstax-gray uppercase tracking-tighter px-1">
              <span>{t.lowSatisfaction}</span>
              <span>{t.highSatisfaction}</span>
            </div>
          </div>

          <div className="space-y-2">
            <label htmlFor="review_text" className="text-xs font-bold text-pstax-gray uppercase tracking-widest px-1">
              {t.reviewText}
            </label>
            <textarea
              id="review_text"
              maxLength={300}
              rows={3}
              value={reviewText}
              onChange={(e) => setReviewText(e.target.value)}
              className="w-full px-4 py-3 bg-pstax-bg/50 border border-pstax-border rounded-xl focus:outline-none focus:border-pstax-blue focus:ring-4 focus:ring-pstax-blue/10 transition-all font-medium resize-none"
              placeholder={t.reviewTextPlaceholder}
            />
            <div className="text-right text-[10px] font-bold text-pstax-gray opacity-50 uppercase">
              {reviewText.length} / 300
            </div>
          </div>

          <div 
            className="flex items-start gap-4 p-4 rounded-2xl border border-pstax-border hover:border-pstax-blue/30 transition-colors cursor-pointer group"
            onClick={() => setConsentPublish(!consentPublish)}
          >
            <div className={`mt-1 w-5 h-5 rounded border flex items-center justify-center transition-all ${consentPublish ? 'bg-pstax-blue border-pstax-blue' : 'border-pstax-border bg-white group-hover:border-pstax-blue'}`}>
              {consentPublish && <CheckCircle2 className="w-4 h-4 text-pstax-navy" />}
            </div>
            <div className="text-sm">
              <strong className="block text-pstax-navy font-bold mb-1">{t.consent}</strong>
              <span className="text-pstax-gray leading-tight">{t.consentSub}</span>
            </div>
          </div>

          <div className="space-y-2">
            <label htmlFor="feedback_short" className="text-xs font-bold text-pstax-gray uppercase tracking-widest px-1 block">
              <span className="block">{t.feedbackShort}</span>
              <span className="block text-[11px] font-normal text-pstax-gray/70 normal-case tracking-normal mt-1">{t.feedbackShortSub}</span>
            </label>
            <textarea
              id="feedback_short"
              maxLength={200}
              rows={2}
              value={feedbackShort}
              onChange={(e) => setFeedbackShort(e.target.value)}
              className="w-full px-4 py-3 bg-pstax-bg/50 border border-pstax-border rounded-xl focus:outline-none focus:border-pstax-blue focus:ring-4 focus:ring-pstax-blue/10 transition-all font-medium resize-none"
              placeholder={t.feedbackShortPlaceholder}
            />
            <div className="text-right text-[10px] font-bold text-pstax-gray opacity-50 uppercase">
              {feedbackShort.length} / 200
            </div>
          </div>

          <button
            type="submit"
            disabled={satisfaction === null || isSubmitting}
            className="w-full py-5 rounded-full bg-pstax-navy text-white font-bold text-lg transition-all hover:bg-pstax-navy/90 hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-3 active:scale-[0.98]"
          >
            {isSubmitting ? (
              <>
                <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                {t.sending}
              </>
            ) : (
              <>
                <Send size={20} />
                {t.submit}
              </>
            )}
          </button>
        </form>

        <footer className="mt-12 pt-8 border-t border-pstax-border text-center">
          <p className="text-[10px] text-pstax-gray tracking-[0.2em] uppercase font-bold opacity-60">
            &copy; {new Date().getFullYear()} PSTax &bull; {t.footer}
          </p>
        </footer>
      </div>
    </div>
  );
};

export default Feedback;
